import { useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { FileText, Users } from "lucide-react";

import { isEmail } from "@/lib/memberForms";
import { PublicShell } from "../components/PublicShell";
import { BlockedNotice } from "../components/forms";
import { V3Columns, V3Actions, PhotoAside, StatusBanner, DetailRow, DetailList, AsideCard, V3_PRIMARY, V3_SECONDARY } from "../components/v3";

const KINDS = {
  email: { kind: "E-Mail-Adresse bestätigen", next: "Eine neue Bestätigungs-E-Mail anfordern", action: "Neuen Bestätigungslink anfordern", to: "/email-hilfe" },
  passwort: { kind: "Passwort zurücksetzen", next: "Einen neuen Passwort-Link anfordern", action: "Neuen Passwort-Link anfordern", to: "/passwort-vergessen" },
};

// M32 – expired link: what it was for and one clear way forward, no private information.
export function LinkExpired() {
  const [params] = useSearchParams();
  const k = KINDS[params.get("art")] || KINDS.email;
  return (
    <PublicShell title="Bestätigungslink abgelaufen" testid="public-link-expired" intro="Der Link zur E-Mail-Bestätigung kann nicht mehr verwendet werden.">
      <V3Columns layout="detail" aside={<AsideCard icon={FileText} title="Dein Bereich" text={k.kind} testid="link-expired-area" />} main={
        <div data-m="M32">
          <StatusBanner tone="sand" title="Link abgelaufen" text={k.kind} testid="link-expired-banner" />
          <DetailList>
            <DetailRow label="Linkart" value={k.kind} testid="link-expired-kind" />
            <DetailRow label="Status" value="Abgelaufen" />
            <DetailRow label="Konto" value="Noch nicht durch diesen Link bestätigt" />
            <DetailRow label="Nächster Schritt" value={k.next} />
          </DetailList>
          <V3Actions>
            <Link to={k.to} data-testid="link-expired-new" className={V3_PRIMARY}>{k.action}</Link>
            <Link to="/login" data-testid="link-expired-login" className={V3_SECONDARY}>Zurück</Link>
          </V3Actions>
        </div>
      } />
    </PublicShell>
  );
}

const HELP_TEXT = "Diese Schritte helfen, wenn die Bestätigungsnachricht fehlt.";

// M35 – help when the confirmation e-mail did not arrive; resending needs the Core.
export function EmailHelp() {
  const { state } = useLocation();
  const [blocked, setBlocked] = useState(false);
  const address = isEmail(state?.email || "") ? state.email : "Schreibweise deiner E-Mail-Adresse prüfen";
  return (
    <PublicShell title="Bestätigungs-E-Mail nicht erhalten" testid="public-email-help" intro={HELP_TEXT}>
      <V3Columns layout="form" aside={<PhotoAside icon={Users} />} main={
        <div data-m="M35">
          <StatusBanner title="Persönlichen Zugang bestätigen" text={HELP_TEXT} />
          <DetailList>
            <DetailRow label="1. Postfach prüfen" value="Auch den Spamordner ansehen" />
            <DetailRow label="2. Adresse kontrollieren" value={address} testid="email-help-address" />
            <DetailRow label="3. Neue Nachricht" value="Erneut senden anfordern" />
            <DetailRow label="Noch keine Nachricht?" value="Hilfe kontaktieren – nach der Anmeldung unter „Hilfe“" testid="email-help-contact" />
          </DetailList>
          {blocked && <div className="mt-[17px]"><BlockedNotice what="gesendet" testid="email-help-blocked">Der erneute Versand ist noch nicht eingerichtet; eine Wartezeit wird erst mit echtem Versand angezeigt.</BlockedNotice></div>}
          <V3Actions>
            <button type="button" onClick={() => setBlocked(true)} className={V3_PRIMARY} data-testid="email-help-resend">E-Mail erneut senden</button>
            <Link to="/login" className={V3_SECONDARY} data-testid="email-help-back">Zurück</Link>
          </V3Actions>
          <p className="mt-[17px] text-[13px] text-v3-muted">Wir melden dich nicht automatisch für Neuigkeiten an.</p>
        </div>
      } />
    </PublicShell>
  );
}
